import { JSX } from "react";
import { useEffect } from "react";

import { ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { WebStorageStateStore } from "oidc-client-ts";
import { createRoot } from "react-dom/client";
import { AuthProvider, type AuthProviderProps } from "react-oidc-context";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router";

import AxiosInterceptor from "./api/AxiosInterceptors";
import App from "./App";
import { ReactQuerySettings } from "./constants/reactQuerySettings";
import { theme } from "./constants/theme";
import {
  KEYCLOAK_AUTHORITY,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REDIRECT_URI,
  KEYCLOAK_POST_LOGOUT_REDIRECT_URI,
  APP_NAME,
  VITE_SENTRY_DSN,
  API_URL,
} from "./lib/config.helper";

const queryClient = new QueryClient({
  defaultOptions: { queries: ReactQuerySettings },
});

const oidcConfig: AuthProviderProps = {
  authority: KEYCLOAK_AUTHORITY ?? "",
  client_id: KEYCLOAK_CLIENT_ID ?? "",
  redirect_uri: KEYCLOAK_REDIRECT_URI == "" ? window.location.origin + "/authredirect" : KEYCLOAK_REDIRECT_URI,
  silent_redirect_uri: KEYCLOAK_REDIRECT_URI == "" ? window.location.origin + "/authredirect" : KEYCLOAK_REDIRECT_URI,
  post_logout_redirect_uri: KEYCLOAK_POST_LOGOUT_REDIRECT_URI == "" ? window.location.origin : KEYCLOAK_POST_LOGOUT_REDIRECT_URI,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
};

document.title = APP_NAME; // set HTML App Name

Sentry.init({
  dsn: VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [window.location.origin, API_URL],
});

function Index(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider {...oidcConfig}>
        <ThemeProvider theme={theme}>
          <AxiosInterceptor>
            <App />
          </AxiosInterceptor>
        </ThemeProvider>
      </AuthProvider>
      {/*<ReactQueryDevtools initialIsOpen={true} />*/}
    </QueryClientProvider>
  );
}

const container = document.querySelector("#app") as HTMLElement;
const root = createRoot(container);
root.render(<Index />);
